import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { FolderKanban, Settings, Atom, ClipboardList, Earth, HardDrive, ArrowRight, CalendarRange, ListTodo } from "lucide-react";
import KeyRotationBanner from "../components/KeyRotationBanner";
import Feed from "../components/Feed";
import WeekCalendar from "../components/WeekCalendar";
import NewsWidget from "../components/NewsWidget";
import { localStrings, firstName } from "../i18n/homeStrings";

export default function Home() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const s = localStrings(language);

  // Everything except Projects. Projects is not in this list because it is not
  // one of these: it is the reason Core exists, and a grid of equal squares
  // said the opposite.
  //
  // Every tile has a colour of its own, and no two are close: blue projects,
  // coral planning, violet trips, emerald decay, sienna reminders, slate files
  // and settings, gold audit. The colour is how somebody finds a section
  // without reading.
  const tools = [
    {
      id: "planning",
      // The same word in both languages, so it is not in the string tables.
      title: "Planning",
      icon: CalendarRange,
      href: "/planning",
      icon_bg: "bg-[#E63946]",
      tint: "from-red-50 to-white border-red-100 hover:border-red-300",
    },
    {
      id: "trasferte",
      title: t("home.trasferte"),
      icon: Earth,
      href: "/trasferte",
      icon_bg: "bg-[#9B7FD4]",
      tint: "from-violet-50 to-white border-violet-100 hover:border-violet-300",
    },
    {
      id: "decay",
      title: t("home.decayCalculator"),
      icon: Atom,
      href: "/decay",
      icon_bg: "bg-emerald-600",
      tint: "from-emerald-50 to-white border-emerald-100 hover:border-emerald-300",
    },
    {
      id: "reminders",
      title: t("reminders.title"),
      icon: ListTodo,
      href: "/promemoria",
      // REMINDER_COLOR in lib/reminders.js, written out for Tailwind.
      icon_bg: "bg-[#A0522D]",
      tint: "from-orange-50 to-white border-orange-100 hover:border-orange-300",
    },
  ];

  const adminTools = [
    {
      id: "files",
      title: s("files.title"),
      icon: HardDrive,
      href: "/files",
      icon_bg: "bg-slate-700",
      tint: "from-slate-50 to-white border-slate-200 hover:border-slate-400",
    },
    {
      id: "audit",
      title: t("nav.audit"),
      icon: ClipboardList,
      href: "/audit",
      icon_bg: "bg-[#E2B112]",
      tint: "from-amber-50 to-white border-amber-100 hover:border-amber-300",
    },
    {
      id: "settings",
      title: t("home.settings"),
      icon: Settings,
      href: "/settings",
      icon_bg: "bg-slate-600",
      tint: "from-slate-50 to-white border-slate-200 hover:border-slate-400",
    },
  ];

  const secondaryTools = [
    ...tools,
    ...(user?.role === "admin" ? adminTools : []),
  ];

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          {/* First name only. The account holds the full name because that is
              what identifies somebody everywhere else; a greeting is the one
              place it reads wrong. */}
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">
            {t("home.welcome")} {firstName(user?.display_name)}
          </h1>

          <KeyRotationBanner />

          {/* Activity and the calendar share the top row. The band no longer
              needs the full width — one project's worth of detail fits in two
              thirds — and the space it gives up is enough for the calendar,
              which would otherwise be pushed below the fold. They stack on a
              phone, activity first. */}
          <div className="grid gap-4 lg:grid-cols-3 items-stretch">
            <div className="lg:col-span-2 min-w-0">
              <Feed />
            </div>
            <div className="min-w-0">
              <WeekCalendar />
            </div>
          </div>

          {/* Projects, at the weight it deserves. Everything else in Core
              supports this one, and a row of identical squares made it look
              like one option among several. */}
          <Link
            to="/projects"
            data-testid="tool-card-projects"
            className="group block rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-blue-50/40 to-white p-6 sm:p-7 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-blue-300"
          >
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/25 shrink-0 group-hover:shadow-xl transition-shadow">
                <FolderKanban className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 min-w-0 flex-1">
                {t("home.projects")}
              </h2>
              <ArrowRight className="w-5 h-5 text-blue-400 shrink-0 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>

          {/* The rest: smaller, because they are. Seven for an administrator,
              four for everyone else, so the grid tops out at seven across. */}
          <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-7">
            {secondaryTools.map((tool) => (
              <Link
                key={tool.id}
                to={tool.href}
                data-testid={`tool-card-${tool.id}`}
                className={`group rounded-2xl border bg-gradient-to-b ${tool.tint} p-4 flex flex-col items-center text-center transition-all hover:-translate-y-0.5 hover:shadow-md`}
              >
                <div
                  className={`w-12 h-12 rounded-xl ${tool.icon_bg} flex items-center justify-center mb-2.5 shadow group-hover:shadow-lg transition-shadow`}
                >
                  <tool.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-slate-800 leading-tight">
                  {tool.title}
                </h3>
              </Link>
            ))}
          </div>

          {/* Last, and full width: it is the one thing here that is not about
              AMMO, so it reads after everything that is. */}
          <NewsWidget />
        </div>
      </main>
    </div>
  );
}
